import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IconTooltip from 'components/ui/base/tooltip/IconTooltip';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import { StyledInfoCircleOutlined } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import {
    FieldWrapper,
    InputLabel,
    InputLabelRequired,
    LabelDiv,
    MetaSection,
    RefDiv,
} from '../formInput/DeclarationInput.styles';
import { CombinedFieldItems } from './styles';

interface CombinedFieldProps {
    label?: string;
    refNumber?: string | string[];
    tooltip?: string;
    refClicked?: (refNumber: string | number) => void;
    required?: boolean;
    condensed?: boolean;
    viewOnly?: boolean;
    children: ReactNode;
}

// Groups several form elements (e.g. a set of related selects) under one shared
// ref number / label / tooltip, taking up the same grid cell a single field would.
const CombinedField: FC<CombinedFieldProps> = ({
    label,
    refNumber: refNumberProp,
    tooltip,
    refClicked,
    required,
    condensed = true,
    viewOnly = false,
    children,
}) => {
    const { t } = useTranslation('common');
    const { template } = useTemplateContext();

    const refNumber = useMemo(() => {
        if (Array.isArray(refNumberProp)) {
            return (
                <>
                    {refNumberProp.map((ref) => (
                        <RefDiv key={`${label}-${ref}`}>{ref}</RefDiv>
                    ))}
                </>
            );
        } else if (typeof refNumberProp === 'string') {
            return <RefDiv>{refNumberProp}</RefDiv>;
        }
    }, [refNumberProp, label]);

    const handleRefClick = useCallback(() => {
        if (!refClicked || Array.isArray(refNumberProp)) return;
        refClicked(refNumberProp ?? label ?? '');
    }, [refClicked, refNumberProp, label]);

    const showTooltip = !!tooltip;

    return (
        <FieldWrapper $viewOnly={viewOnly} $template={template}>
            <MetaSection>
                <LabelDiv condensed={condensed}>
                    {refNumber}

                    {label && (
                        <InputLabel>
                            {required && <InputLabelRequired right>*</InputLabelRequired>}
                            {label}
                        </InputLabel>
                    )}

                    {showTooltip && (
                        <IconTooltip
                            title={label}
                            tooltip={tooltip ?? t('defaultTooltip')}
                            icon={<StyledInfoCircleOutlined />}
                            tooltipClicked={handleRefClick}
                        />
                    )}
                </LabelDiv>
            </MetaSection>
            <CombinedFieldItems>{children}</CombinedFieldItems>
        </FieldWrapper>
    );
};

export default CombinedField;
